import React, {
  ChangeEvent,
  FormEvent,
  createRef,
  useState,
  useEffect,
} from 'react'
import Fuse, { FuseOptions } from 'fuse.js'
import gql from 'graphql-tag'
import styled from 'styled-components'
import { useTheme } from '~/common/theme'
import SharedIcon from 'shared/timber/v1/Icon'

import { SIZES } from '~/common/globalStyles'
import {
  TagType,
  ProjectType,
  QueryAvailableTagsArgs,
  TemplateType,
  TagsCreateAvailableTagMutation,
  TagsCreateAvailableTagMutationVariables,
  TagsAvailableTagsQuery,
  TagsAvailableTagsQueryVariables,
  TaggableEnumType,
  Maybe,
  AddTagInput,
  TagColorItemAddTagMutation,
  TagColorItemAddTagMutationVariables,
} from '~/common/types/graphql'
import { FormBody } from '~/views/components/elements/Forms'
import Button from '~/views/components/ui/Button'
import Icon from '~/views/components/ui/Icon'
import TextField from '~/views/components/ui/inputs/TextField'
import ViewerAuthorized from '~/views/components/ViewerAuthorized'
import useErrorHandlingMutation from '~/views/hooks/useErrorHandlingMutation'

import ColorDropdown from './components/ColorDropdown'
import TagColorItem, { TagColorItemFragments } from './components/TagColorItem'
import { Tag } from './types'
import { useCurrentUserProjectContext } from '~/views/contexts/CurrentUserProjectContext'

import { SearchIconWrapper } from './styled'

export const TagsFragments = {
  availableTags: gql`
    fragment Tags_AvailableTags on TagType {
      id
      ...TagColorItem_Tag
    }
    ${TagColorItemFragments.tag}
  `,
}

const AVAILABLE_TAGS_QUERY = gql`
  query Tags_AvailableTags(
    $taggableId: ID!
    $type: String
    $taggableType: TaggableEnumType!
  ) {
    availableTags(
      taggableId: $taggableId
      type: $type
      taggableType: $taggableType
    ) {
      ...Tags_AvailableTags
    }
  }
  ${TagsFragments.availableTags}
`

const addTagMutation = gql`
  mutation TagColorItem_AddTag($tagInput: addTagInput!) {
    addTag(input: $tagInput) {
      taggable {
        ... on TaskType {
          id
          tags {
            ...TagColorItem_Tag
          }
        }
        ... on ProjectType {
          id
          tags {
            ...TagColorItem_Tag
          }
        }
        ... on ActionItemType {
          id
          tags {
            ...TagColorItem_Tag
          }
        }
      }
    }
  }
  ${TagColorItemFragments.tag}
`

const CREATE_AVAILABLE_TAG_MUTATION = gql`
  mutation Tags_CreateAvailableTag(
    $availableTagInput: createAvailableTagInput!
  ) {
    createAvailableTag(input: $availableTagInput) {
      tag {
        ...Tags_AvailableTags
      }
    }
  }
  ${TagsFragments.availableTags}
`

const AdjustedFormBody = styled(FormBody)`
  &&& > * + * {
    margin-top: ${SIZES.xs} !important;
  }
`

const ColorField = styled.div`
  display: grid;
  grid-template-columns: 1fr 90px;
  grid-column-gap: ${SIZES.large};
  grid-row-gap: ${SIZES.xs};
`

const ColorsList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 31px;
  grid-row-gap: ${SIZES.medium};
  padding: ${SIZES.xl};
`
const SearchContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 31px;
  grid-row-gap: ${SIZES.medium};
  margin: 0 ${SIZES.xl};
`

const IconSearch = styled(Icon)`
  align-self: center;
  margin-right: ${SIZES.tiny};
`

const IconAdd = styled(Icon)`
  margin-top: ${SIZES.tiny};
  margin-left: ${SIZES.tiny};
`

const ButtonCreate = styled(Button)(
  ({ theme }) => `
    && {
      display: flex;
      padding: 0 0 0 ${theme.spacing(0.5)};
    }
`,
)

export interface AvailableTagsData {
  availableTags?: Maybe<Array<TagType>>
}

interface Props {
  availableTags: AvailableTagsData
  tags: Maybe<Array<TagType>>
  taggableId: ProjectType['id'] | undefined
  editRoles: string[]
  showErrors?(message: string): void
  templateId?: TemplateType['id']
  taskType?: QueryAvailableTagsArgs['type']
  taggableType: TaggableEnumType
  onSuccessfulTagsUpdate?: () => void
}

const Tags = ({
  availableTags: propAvailableTags,
  tags: propTags,
  taggableId = '',
  editRoles,
  showErrors,
  taskType,
  taggableType,
  onSuccessfulTagsUpdate,
}: Props) => {
  const defaultColorValue = 'green'

  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [availableTagsFiltered, setAvailableTagsFiltered] = useState<Tag[]>([])

  const { currentUserProjectContext } = useCurrentUserProjectContext()

  const theme = useTheme()

  useEffect(() => {
    const initAvailableTag = () => {
      const tags = propTags || []
      const tagsIdList = tags.map((tag: TagType) => tag.id)
      const rawTags = propAvailableTags.availableTags || []

      setAvailableTags(
        rawTags.map((tag: TagType) => ({
          ...tag,
          checked: tagsIdList.includes(tag.id),
        })),
      )
    }
    initAvailableTag()
  }, [propAvailableTags, propTags])

  const [color, setColor] = useState(defaultColorValue)
  const [name, setName] = useState('')
  const [search, setSearch] = useState('')

  const [, setFocusInputField] = useState(createRef())

  const searchOptions: FuseOptions<Tag> = {
    threshold: 0.3,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: ['name'],
  }

  const fuse = new Fuse(availableTags, searchOptions)

  const [onCreateAvailableTag] = useErrorHandlingMutation<
    TagsCreateAvailableTagMutation,
    TagsCreateAvailableTagMutationVariables
  >(CREATE_AVAILABLE_TAG_MUTATION)

  const [onAddTagMutation] = useErrorHandlingMutation<
    TagColorItemAddTagMutation,
    TagColorItemAddTagMutationVariables
  >(addTagMutation)

  const createAvailableTag = async (event: FormEvent<HTMLFormElement>) => {
    event.stopPropagation()
    event.preventDefault()

    if (!name) return null

    return await onCreateAvailableTag({
      variables: {
        availableTagInput: { color, name, taggableId, taggableType },
      },
      refetchQueries: ['Tags_AvailableTags'],
      update: (cache, mutationResult) => {
        const tag =
          mutationResult &&
          mutationResult.data &&
          mutationResult.data.createAvailableTag &&
          mutationResult.data.createAvailableTag.tag

        if (tag) {
          const availableTagsResult = cache.readQuery<
            TagsAvailableTagsQuery,
            TagsAvailableTagsQueryVariables
          >({
            query: AVAILABLE_TAGS_QUERY,
            variables: { taggableId, type: taskType, taggableType },
          })
          if (availableTagsResult) {
            const { availableTags: cachedAvailableTags } = availableTagsResult
            cachedAvailableTags &&
              cache.writeQuery<
                TagsAvailableTagsQuery,
                TagsAvailableTagsQueryVariables
              >({
                query: AVAILABLE_TAGS_QUERY,
                variables: {
                  taggableId,
                  type: taskType,
                  taggableType,
                },
                data: {
                  availableTags: cachedAvailableTags.concat([tag]),
                },
              })
          }

          const newAvailableTags = [...availableTags]
          newAvailableTags.push(tag as Tag)
          setAvailableTags(newAvailableTags)
          setColor(defaultColorValue)
          setName('')
          onAddTag(tag.id)
        }
      },
    })
  }

  const toggleTagPrivacy = (id: Tag['id']) => () => {
    setAvailableTags(
      availableTags.map((tag) => {
        if (tag.id === id) tag.internalOnly = !tag.internalOnly
        return tag
      }),
    )
  }

  const toggleChecked = (id: Tag['id']) => () => {
    setAvailableTags(
      availableTags.map((tag) => {
        if (tag.id === id) tag.checked = !tag.checked
        return tag
      }),
    )
  }

  const removeTag = (id: Tag['id']) => () => {
    setAvailableTags(availableTags.filter((tag) => tag.id !== id))
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setName(value)
  }

  const editTag = (tagData: Tag) => {
    const availableTagsCopy: Tag[] = [...availableTags]
    const index = availableTagsCopy.findIndex((t) => tagData.id === t.id)
    const tag = availableTagsCopy[index]

    if (tag) {
      tag.name = tagData.name
      tag.color = tagData.color
    }

    setAvailableTags(availableTagsCopy)
  }

  const onAddTag = async (tagId: Tag['id']) => {
    await onAddTagMutation({
      variables: {
        tagInput: { tagId, taggableId, taggableType } as AddTagInput,
      },
      refetchQueries: ['ActionItemQuery_ActionItem'],
    })
      .then(() => {
        onSuccessfulTagsUpdate?.()
      })
      .catch(() => {
        toggleChecked(tagId)
        showErrors && showErrors('There was an error adding this tag.')
      })
  }

  useEffect(() => {
    const fuzzySearch = (searchTmp: string) => {
      searchTmp = searchTmp.trim()
      setAvailableTagsFiltered(
        searchTmp === '' ? availableTags : (fuse.search(searchTmp) as Tag[]),
      )
    }
    fuzzySearch(search)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, availableTags])

  return (
    <div style={{ overflowX: 'hidden', height: '100%' }}>
      <AdjustedFormBody onSubmit={() => false}>
        <ViewerAuthorized
          roles={editRoles}
          currentUserProjectContext={currentUserProjectContext}
        >
          <ColorField>
            <TextField
              dataTestId="tagNameInput"
              required
              fullWidth
              value={name}
              placeholder="Create a New Tag"
              onChange={onChange}
              inputRef={(field: any) => setFocusInputField(field)}
            />
            <ColorDropdown
              color={color}
              updateValue={(value: string) => {
                setColor(value)
              }}
            />
            <div style={{ gridRow: 2, gridColumn: 2 }}>
              <ButtonCreate
                raised
                onClick={createAvailableTag}
                data-testid="create-tag-button"
              >
                Create <IconAdd name="plus" />
              </ButtonCreate>
            </div>
          </ColorField>
        </ViewerAuthorized>
      </AdjustedFormBody>
      <SearchContainer>
        <TextField
          dataTestId="searchTagName"
          fullWidth
          type="search"
          placeholder="Search by Name"
          icon={
            theme.name === 'lightV2' ? (
              <SearchIconWrapper color={theme.palette.grey[500]}>
                <SharedIcon icon="search_sm" size="xs" />
              </SearchIconWrapper>
            ) : (
              <IconSearch
                name="search"
                width={SIZES.large}
                color="lighterGray"
              />
            )
          }
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            event.persist()
            setSearch(event.target.value)
          }}
          shrink={false}
        />
      </SearchContainer>
      <ColorsList>
        {availableTags.length === 0 ? (
          <div style={{ textAlign: 'center' }}>
            You don&apos;t have any Tags, add some below or contact your project
            manager.
          </div>
        ) : null}
        {availableTagsFiltered.map((availableTag) => (
          <TagColorItem
            showErrors={showErrors}
            onAddTag={onAddTag}
            removeTag={removeTag(availableTag.id)}
            toggleChecked={toggleChecked(availableTag.id)}
            toggleTagPrivacy={toggleTagPrivacy(availableTag.id)}
            editTag={editTag}
            taggableId={taggableId}
            key={availableTag.id}
            tag={availableTag}
            taggableType={taggableType}
            onSuccessfulTagsUpdate={onSuccessfulTagsUpdate}
          />
        ))}
      </ColorsList>
    </div>
  )
}

export default Tags
